import { type Ref, computed, reactive, ref } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import {
  type AIAssistantMessage,
  isAIAssistantModelMessage,
} from "shared/composables/useAIAssistant";

export interface AIMentionStreamAgentResponse {
  response: string;
  session_id: string;
}

export function useAIMentionStreamAgent(
  options: {
    source: string;
  },
  chatSessionId: Ref<string>
) {
  const messages = ref<AIAssistantMessage[]>([]);

  const loading = computed(() => {
    const lastModelMessage = messages.value
      .toReversed()
      .find((message) => isAIAssistantModelMessage(message));

    if (!lastModelMessage) return false;

    return lastModelMessage.stream.loading;
  });

  function add(message: AIAssistantMessage | AIAssistantMessage[]) {
    messages.value = messages.value.concat(message);
  }

  async function submit(prompt: string) {
    const modelMessageStream = reactive({
      data: {
        ai_feature_configuration_id: null,
        group_id: null,
        source: options.source,
      },
      booleanQuery: "",
      error: false,
      input: "",
      json: "{}",
      loading: true,
      message: "",
      rateLimitReached: false,
    });

    add([
      {
        role: "user",
        message: prompt,
      },
      {
        role: "model",
        message: "",
        stream: modelMessageStream,
      },
    ]);

    const responseData = await fetchResponse(prompt);
    Object.assign(modelMessageStream, responseData);
  }

  async function fetchResponse(prompt: string) {
    let message = "";
    let error = false;
    let rateLimitReached = false;

    try {
      const response = await $streemApiV1.post<AIMentionStreamAgentResponse>(
        "ai/services/mention_stream_agent",
        {
          params: {
            session_id: chatSessionId.value,
            prompt,
          },
        }
      );

      const responseData = response.data;

      message = responseData.response;
      // eslint-disable-next-line no-param-reassign
      chatSessionId.value = responseData.session_id;
    } catch (err: any) {
      error = true;

      if (err.response.status === 429) {
        rateLimitReached = true;
      }
    }

    return {
      data: {
        ai_feature_configuration_id: null,
        group_id: null,
        source: options.source,
      },
      booleanQuery: "",
      error,
      input: "",
      json: "{}",
      loading: false,
      message,
      rateLimitReached,
    };
  }

  return {
    loading,
    messages,

    add,
    submit,
  };
}
